<script async setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref, watch, watchEffect } from 'vue';
import { apps, contextKeys, useContext, useLiveQueries, useProcesses } from '@hantera/portal-app';

import Order, { OrderViewContextImpl } from '@/components/Order';
import useBackend from "@/composition/useBackend";
import { createKeyboardCaptureContext } from "@/composition/keyboard";
import useApps from "@/composition/use-apps";



const backend = useBackend();
const context = useContext()

const notFound = ref(false);

const orderSelector = computed<{
  type: 'orderId' | 'orderNumber'
  value: string
}>(() => {
  const currentView = context.currentView.value!
  if (currentView.query.id) {
    return {
      type: 'orderId',
      value: currentView.query.id
    }
  }
  return {
    type: 'orderNumber',
    value: currentView.query.number!
  }
})

const appsContext = useApps()
const queryExtensions = appsContext
  .getServices(apps.standardServices.orderView.queryExtensionProvider)
  .map(provider => provider.extendOrderQuery())

const orderViewContext = new OrderViewContextImpl(
  orderSelector.value,
  backend.fetch,
  useProcesses(),
  useLiveQueries(),
  queryExtensions
);


try {
  await orderViewContext.refresh();
  orderViewContext.startLiveUpdates();

  onMounted(() => {
    window.addEventListener('beforeunload', leaveConfirm);
  });
  onUnmounted(() => {
    window.removeEventListener('beforeunload', leaveConfirm);
    orderViewContext.dispose();
  });


  createKeyboardCaptureContext(
    _ => context.currentView.value!.active
  );

  watchEffect(() => {
    context.currentView.value!.hasPendingChanges.value = orderViewContext.historyState.hasPending;
  });

  provide(contextKeys.orderView, orderViewContext);
  context.currentView.value!.name.value = orderViewContext.state.orderNumber;

  async function leaveConfirm(event: BeforeUnloadEvent) {
    if (orderViewContext.historyState.hasPending) {
      event.preventDefault();  // This is needed for modern browsers
      // Chrome requires returnValue to be set, although the string is ignored
      event.returnValue = '';  // Leaving it as an empty string for compatibility
    }
  }
}
catch (err: any) {
  // TODO Display error
  console.error(err);
  notFound.value = true;
}

const selectedTab = ref(context.currentView.value!.state.selectedTab)

watch(selectedTab, () => {
  context.currentView.value!.state.selectedTab = selectedTab
})

</script>

<template>

  <template v-if="notFound">
    Order not found
  </template>
  <template v-else>
    <Order v-model:selected-tab="selectedTab" class="workspace-order neat-scroll"/>
  </template>

</template>

<style scoped>
.workspace-order {
  height: 100%;
  overflow-y: auto;
  display: grid;
  grid-template-rows: min-content auto;
}

.workspace-order :deep(.tabs) {
  display: grid;
  grid-template-rows: min-content auto;
  overflow: hidden;
}
</style>